import { Flex, Typography } from 'antd'
import { KeyRound, ShieldCheck, Users } from 'lucide-react'
import type { PropsWithChildren, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useDocumentTitle } from 'src/hooks'
import { LanguageSwitcher } from 'src/layout/AppLayoutShell'
import { PublicSplitShell } from 'src/layout/PublicSplitShell'
import { isAuthLoginPath } from 'src/router'

import { useQrLoginAvailability } from '../../hooks'

import { useStyles } from './styles'
import { getAuthPageTitle } from './utils'

const { Title, Text } = Typography

/** Compact reading width for a form; the login routes need room for the QR. */
const FORM_WIDTH = 340

const FORM_WIDTH_WITH_QR = 620

const HERO_FEATURES: { key: string; icon: ReactNode }[] = [
  {
    key: 'auth_hero_feature_sso',
    icon: <KeyRound size={16} strokeWidth={1.5} />
  },
  {
    key: 'auth_hero_feature_orgs',
    icon: <Users size={16} strokeWidth={1.5} />
  },
  {
    key: 'auth_hero_feature_security',
    icon: <ShieldCheck size={16} strokeWidth={1.5} />
  }
]

export const LayoutAuth = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation()
  const { styles } = useStyles()

  const { pathname } = useLocation()
  useDocumentTitle(getAuthPageTitle(pathname, t))

  const { isQrEnabled, isQrOnly } = useQrLoginAvailability()
  // The QR panel renders beside the form (in Login) on the login routes; widen
  // the form column so it has room next to the QR.
  const showInlineQr = isAuthLoginPath(pathname) && isQrEnabled && !isQrOnly

  return (
    <PublicSplitShell
      contentMaxWidth={showInlineQr ? FORM_WIDTH_WITH_QR : FORM_WIDTH}
      footerControls={<LanguageSwitcher />}
      aside={
        <Flex vertical gap={28} className={styles.heroContent}>
          <Flex vertical gap={12}>
            <Title level={2} className={styles.heroTitle}>
              {t('auth_hero_title')}
            </Title>
            <Text className={styles.heroSubtitle}>
              {t('auth_hero_subtitle')}
            </Text>
          </Flex>

          <Flex vertical gap={16}>
            {HERO_FEATURES.map((feature) => (
              <Flex key={feature.key} gap={12} align="center">
                <span className={styles.heroFeatureIcon}>{feature.icon}</span>
                <Text className={styles.heroFeatureText}>{t(feature.key)}</Text>
              </Flex>
            ))}
          </Flex>
        </Flex>
      }
    >
      {children}
    </PublicSplitShell>
  )
}
